import { useState } from 'react';
import { Box, Heading, Text, VStack, HStack, Button, Image, Badge } from '@chakra-ui/react';
import { FiDownload, FiExternalLink } from 'react-icons/fi';

interface SoilData {
  id: string;
  type: 'soilpit' | 'photo' | 'publication' | 'observation';
  name: string;
  description: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  uploader: string;
  uploadDate: string;
  files?: {
    images: string[];
    pdfs: string[];
  };
  soilProperties?: {
    classification: string;
    greatGroup: string;
    subGroup: string;
    drainage: string;
    texture: string;
    horizons: Array<{
      designation: string;
      depth: string;
      texture: string;
      color: string;
    }>;
  };
}

interface EnhancedSoilInfoProps {
  soilData: SoilData;
  onClose: () => void;
}

const EnhancedSoilInfo = ({ soilData, onClose }: EnhancedSoilInfoProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'soilpit': return 'blue';
      case 'photo': return 'green';
      case 'publication': return 'purple';
      case 'observation': return 'orange';
      default: return 'gray';
    }
  };

  const downloadFile = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Safely remove the element if it still exists in the DOM
    if (document.body.contains(link)) {
      document.body.removeChild(link);
    }
  };

  return (
    <Box p={6} maxW="600px" mx="auto" borderWidth="1px" borderRadius="lg" bg="white">
      <VStack align="stretch" gap={4}>
        <HStack justify="space-between">
          <Heading size="md">{soilData.name}</Heading>
          <Badge colorScheme={getTypeColor(soilData.type)}>
            {soilData.type.toUpperCase()}
          </Badge>
        </HStack>

        <Text color="gray.600">{soilData.description}</Text>

        <HStack>
          <Text fontSize="sm" color="gray.500">
            Coordinates: {soilData.coordinates.lat.toFixed(4)}, {soilData.coordinates.lng.toFixed(4)}
          </Text>
        </HStack>

        <HStack>
          <Text fontSize="sm" color="gray.500">
            Uploaded by: {soilData.uploader}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Date: {new Date(soilData.uploadDate).toLocaleDateString()}
          </Text>
        </HStack>

        <Box>
          {soilData.soilProperties && (
            <Box mb={6}>
              <Heading size="sm" mb={3}>Soil Properties</Heading>
                <VStack align="stretch" gap={3}>
                  <HStack>
                    <Text fontWeight="medium">Classification:</Text>
                    <Text>{soilData.soilProperties.classification}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium">Great Group:</Text>
                    <Text>{soilData.soilProperties.greatGroup}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium">Sub Group:</Text>
                    <Text>{soilData.soilProperties.subGroup}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="medium">Drainage:</Text>
                    <Text>{soilData.soilProperties.drainage}</Text>
                  </HStack>
                  
                  {soilData.soilProperties.horizons && soilData.soilProperties.horizons.length > 0 && (
                    <Box>
                      <Text fontWeight="medium" mb={2}>Horizons:</Text>
                      <VStack align="stretch" gap={2}>
                        {soilData.soilProperties.horizons.map((horizon, index) => (
                          <Box key={index} p={3} bg="gray.50" borderRadius="md">
                            <HStack justify="space-between">
                              <Text fontWeight="medium">{horizon.designation}</Text>
                              <Text fontSize="sm">{horizon.depth}</Text>
                            </HStack>
                            <HStack>
                              <Text fontSize="sm">Texture: {horizon.texture}</Text>
                              <Text fontSize="sm">Color: {horizon.color}</Text>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
            </Box>
          )}

          {soilData.files?.images && soilData.files.images.length > 0 && (
            <Box mb={6}>
              <Heading size="sm" mb={3}>Images</Heading>
              <VStack align="stretch" gap={3}>
                {soilData.files.images.map((imageUrl, index) => (
                  <Box key={index}>
                    <Image
                      src={imageUrl}
                      alt={`Soil image ${index + 1}`}
                      maxH="200px"
                      objectFit="cover"
                      cursor="pointer"
                      onClick={() => setSelectedImage(imageUrl)}
                      borderRadius="md"
                    />
                    <Button
                      size="sm"
                      bg="blue.500"
                      color="white"
                      _hover={{ bg: "blue.600" }}
                      onClick={() => downloadFile(imageUrl, `soil-image-${index + 1}.jpg`)}
                      mt={2}
                    >
                      <FiDownload style={{ marginRight: '8px' }} />
                      Download
                    </Button>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {soilData.files?.pdfs && soilData.files.pdfs.length > 0 && (
            <Box mb={6}>
              <Heading size="sm" mb={3}>Documents</Heading>
              <VStack align="stretch" gap={3}>
                {soilData.files.pdfs.map((pdfUrl, index) => (
                  <HStack key={index} justify="space-between" p={3} bg="gray.50" borderRadius="md">
                    <Text>Document {index + 1}</Text>
                    <HStack>
                      <Button
                        size="sm"
                        bg="blue.500"
                        color="white"
                        _hover={{ bg: "blue.600" }}
                        onClick={() => window.open(pdfUrl, '_blank')}
                      >
                        <FiExternalLink style={{ marginRight: '8px' }} />
                        View
                      </Button>
                      <Button
                        size="sm"
                        bg="blue.500"
                        color="white"
                        _hover={{ bg: "blue.600" }}
                        onClick={() => downloadFile(pdfUrl, `document-${index + 1}.pdf`)}
                      >
                        <FiDownload style={{ marginRight: '8px' }} />
                        Download
                      </Button>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
        </Box>

        <Button onClick={onClose} mt={4}>
          Close
        </Button>
      </VStack>

      {selectedImage && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.800"
          zIndex={1000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => setSelectedImage(null)}
        >
          <Image
            src={selectedImage}
            maxW="90vw"
            maxH="90vh"
            objectFit="contain"
          />
        </Box>
      )}
    </Box>
  );
};

export default EnhancedSoilInfo;
